import { CONTACTS_BULK_TEMPLATE } from '../bulk-action-templates';
import type {
	FieldConfiguration,
	ListBuilderColumn,
	ListBuilderConfiguration,
	ListConfiguration,
} from '../types';

/**
 * Contacts page (Sales -> Customers -> Contacts), PD-17065: one row per PERSON
 * (`contact`), not per customer record. Columns stack two or three values like
 * the Clients list; every value comes from the person's stored summaries or
 * one indexed join, so the list stays fast over hundreds of thousands of
 * people (plan: docs/claude/sales-relationships/01-contacts.md).
 *
 * Data source: GET /contacts (ContactsListService). Tab rules use the filter
 * keys in libs/core/src/filters/config/client-contact.ts.
 */

const lineBreak = (id: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
});

// Optional lines in a stacked cell are hidden when empty, so a row shows only
// what the person actually has instead of a dash per missing value; the
// column's main value still shows a dash when it is empty. The API returns
// null (never '') for a blank value.
const whenSet = (fieldName: string): FieldConfiguration['conditionalVisibility'] => ({
	field: fieldName,
	condition: 'is_not_null',
});

const secondary = (id: string, fieldName: string, displayStyle: FieldConfiguration['displayStyle'] = 'text'): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable: 'contact' } },
	styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
	displayStyle,
	conditionalVisibility: whenSet(fieldName),
});

export const createContactColumns = (): Array<ListBuilderColumn> => [
	{
		id: 'contact-column',
		fields: [
			{
				id: 'contact-active-dot',
				dataConfig: { type: 'field', field: { fieldName: 'isActive', objectTable: 'contact' } },
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					valueMapping: [
						{ color: { themeColor: 'success' }, value: 'true' },
						{ color: { themeColor: 'success' }, value: '1' },
						{ color: { themeColor: 'textSecondary' }, value: 'false' },
						{ color: { themeColor: 'textSecondary' }, value: '0' },
					],
				},
				displayStyle: 'dot',
				floatingLeft: true,
			},
			{
				id: 'contact-name',
				link: { type: 'custom', enabled: true, urlTemplate: '/clients/contacts/{id}' },
				dataConfig: { type: 'field', field: { fieldName: 'name', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
			lineBreak('contact-lb-1'),
			secondary('contact-title', 'titleLabel'),
			lineBreak('contact-lb-2'),
			{
				id: 'contact-roles',
				dataConfig: { type: 'field', field: { fieldName: 'roleSummary', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'info' } },
				displayStyle: 'badge',
				conditionalVisibility: whenSet('roleSummary'),
			},
		],
		headers: [{ id: 'h-contact', text: 'Contact' }],
		minWidth: 200,
		sortable: true,
		alignment: 'left',
		sortFields: [{ direction: 'ASC', fieldName: 'name', objectTable: 'contact' }],
		widthConfig: { mode: 'flexible', preset: 'large' },
		verticalAlignment: 'top',
	},
	{
		id: 'customers-column',
		fields: [
			{
				id: 'primary-customer',
				link: { type: 'custom', enabled: true, urlTemplate: '/clients/{primaryClientId}' },
				dataConfig: { type: 'field', field: { fieldName: 'primaryClientName', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
			lineBreak('customers-lb-1'),
			{
				id: 'other-customers',
				dataConfig: { type: 'field', field: { fieldName: 'otherCustomersLabel', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'info' } },
				displayStyle: 'text',
				conditionalVisibility: whenSet('otherCustomersLabel'),
			},
			lineBreak('customers-lb-2'),
			secondary('customer-city-state', 'primaryClientCityState'),
		],
		headers: [{ id: 'h-customers', text: 'Customers' }],
		minWidth: 180,
		sortable: true,
		alignment: 'left',
		sortFields: [{ direction: 'ASC', fieldName: 'primaryClientName', objectTable: 'contact' }],
		widthConfig: { mode: 'flexible', preset: 'large' },
		verticalAlignment: 'top',
	},
	{
		id: 'phone-email-column',
		fields: [
			{
				id: 'contact-mobile',
				dataConfig: { type: 'field', field: { fieldName: 'mobilePhone', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'phone',
				conditionalVisibility: whenSet('mobilePhone'),
			},
			lineBreak('phone-lb-1'),
			{
				id: 'contact-phone',
				dataConfig: { type: 'field', field: { fieldName: 'phone', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'phone',
				conditionalVisibility: whenSet('phone'),
			},
			lineBreak('phone-lb-2'),
			{
				id: 'contact-email',
				dataConfig: { type: 'field', field: { fieldName: 'email', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'email',
			},
			lineBreak('phone-lb-3'),
			{
				id: 'contact-flags',
				dataConfig: { type: 'field', field: { fieldName: 'contactFlagsLabel', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'danger' } },
				displayStyle: 'text',
				conditionalVisibility: whenSet('contactFlagsLabel'),
			},
		],
		headers: [
			{ id: 'h-phone', text: 'Phone' },
			{ id: 'h-phone-lb', text: '' },
			{ id: 'h-email', text: 'Email' },
		],
		minWidth: 180,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'salespeople-column',
		fields: [
			{
				id: 'salesperson-name',
				dataConfig: { type: 'field', field: { fieldName: 'salespersonName', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
			lineBreak('salespeople-lb-1'),
			secondary('alternate-salesperson-name', 'alternateSalespersonName'),
		],
		headers: [
			{ id: 'h-salesperson', text: 'Salesperson' },
			{ id: 'h-salesperson-lb', text: '' },
			{ id: 'h-alternate', text: 'Alternate' },
		],
		minWidth: 140,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'last-contacted-column',
		fields: [
			{
				id: 'last-contacted-date',
				dataConfig: { type: 'field', field: { fieldName: 'lastContactedDate', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'relative_date',
			},
			lineBreak('last-contacted-lb-1'),
			secondary('last-contacted-label', 'lastContactedLabel'),
		],
		headers: [{ id: 'h-last-contacted', text: 'Last Contacted' }],
		minWidth: 140,
		sortable: true,
		alignment: 'left',
		sortFields: [{ direction: 'DESC', fieldName: 'lastContactedDate', objectTable: 'contact' }],
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		// PD-17132 — the soonest open task about the person (a task's "About").
		id: 'next-task-column',
		fields: [
			{
				id: 'next-task-date',
				dataConfig: { type: 'field', field: { fieldName: 'nextTaskDueDate', objectTable: 'contact' } },
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h-next-task', text: 'Next Task' }],
		minWidth: 110,
		sortable: true,
		alignment: 'left',
		sortFields: [{ direction: 'ASC', fieldName: 'nextTaskDueDate', objectTable: 'contact' }],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'actions-column',
		fields: [
			{
				id: 'actions-dropdown',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'actions_dropdown',
				dropdownActions: [
					{
						id: 'view-action',
						label: 'View',
						iconName: 'eye',
						callbackName: 'viewContact',
						variant: 'secondary',
						visibility: 'visible',
					},
					{
						id: 'edit-action',
						label: 'Edit',
						iconName: 'edit',
						callbackName: 'editContact',
						variant: 'primary',
						visibility: 'visible',
						requiredPermission: 'clients:contacts:update',
					},
				],
			},
		],
		headers: [{ id: 'h-actions', text: '' }],
		sortable: false,
		widthConfig: { mode: 'fixed', width: 100 },
	},
];

/** A tab: the shared columns plus its own rules. */
const contactList = (list: Omit<ListConfiguration, 'columns' | 'cacheTTL' | 'type' | 'active'>): ListConfiguration => ({
	cacheTTL: 300000,
	type: 'default',
	active: true,
	columns: createContactColumns(),
	defaultSortId: 'contacts-name-asc',
	// Row selection with "Add to Campaign" (plan section 1, Q1-2).
	bulkActionTemplate: CONTACTS_BULK_TEMPLATE,
	...list,
});

const meValues = [
	{ id: 'ME', label: 'Me' },
	{ id: 'ME_ALTERNATE', label: 'Me (Alternate)' },
];

export const CLIENT_CONTACT_CONFIG: ListBuilderConfiguration = {
	lists: [
		contactList({
			id: 'default-contacts-all',
			name: 'All Contacts',
			description: 'Every active contact on the customers you can open',
			sortOrder: 0,
		}),
		contactList({
			id: 'contacts-mine',
			name: 'My Contacts',
			description: 'People at the customers you are the salesperson or alternate for',
			sortOrder: 1,
			filterRules: {
				contactSalespeople: { defaultValue: meValues },
			},
		}),
		contactList({
			id: 'contacts-buyers-decision-makers',
			name: 'Buyers & Decision-makers',
			description: 'Buyers, purchasing, owners and decision-makers',
			sortOrder: 2,
			filterRules: {
				contactRoles: {
					defaultValue: [
						{ id: 'Buyer', label: 'Buyer' },
						{ id: 'Purchasing', label: 'Purchasing' },
						{ id: 'Decision-maker', label: 'Decision-maker' },
						{ id: 'Owner', label: 'Owner' },
					],
				},
			},
		}),
		contactList({
			id: 'contacts-accounts-payable',
			name: 'Accounts Payable',
			description: 'Accounts payable contacts, for collections',
			sortOrder: 3,
			filterRules: {
				contactRoles: { defaultValue: [{ id: 'Accounts Payable', label: 'Accounts Payable' }] },
			},
		}),
		contactList({
			id: 'contacts-due-for-contact',
			name: 'Due for Contact',
			description: 'My contacts not contacted in 90 days',
			sortOrder: 4,
			defaultSortId: 'contacts-last-contacted-asc',
			filterRules: {
				contactSalespeople: { defaultValue: meValues },
				notContactedDays: { defaultValue: [{ id: '90', label: '90 days' }] },
			},
		}),
		contactList({
			id: 'contacts-multi-customer',
			name: 'On Several Customers',
			description: 'People linked to two or more customers (chain buyers, owners)',
			sortOrder: 5,
			defaultSortId: 'contacts-customer-count-desc',
			filterRules: {
				multiCustomer: { defaultValue: [{ id: 'true', label: 'On two or more customers' }] },
			},
		}),
		contactList({
			id: 'contacts-new',
			name: 'New',
			description: 'Added in the last 30 days',
			sortOrder: 6,
			defaultSortId: 'contacts-created-desc',
			filterRules: {
				addedWithinDays: { defaultValue: [{ id: '30', label: 'Last 30 days' }] },
			},
		}),
		contactList({
			id: 'contacts-no-longer-active',
			name: 'No Longer Active',
			description: 'People no longer active at any of their customers',
			sortOrder: 7,
			filterRules: {
				contactStatus: { defaultValue: [{ id: 'inactive', label: 'No longer active' }] },
			},
		}),
		contactList({
			id: 'contacts-do-not-contact',
			name: 'Do Not Contact',
			description: 'Do-not-call or do-not-email',
			sortOrder: 8,
			filterRules: {
				doNotContact: { defaultValue: [{ id: 'true', label: 'Do not call or email' }] },
			},
		}),
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'contactSearch',
			'contactRoles',
			'contactSalespeople',
			'contactClient',
			'contactState',
			'notContactedDays',
			'contactStatus',
			'multiCustomer',
			'addedWithinDays',
			'doNotContact',
			'hasEmail',
			'hasPhone',
			'contactSource',
			'contactKind',
			'campaigns',
		],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Contact',
			objectTable: 'contact',
			baseUrl: '/clients/contacts/:id',
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'title', fieldLabel: 'Title' },
				{ fieldName: 'roleSummary', fieldLabel: 'Roles' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'mobilePhone', fieldLabel: 'Mobile Phone' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'primaryClientName', fieldLabel: 'Main Customer' },
				{ fieldName: 'primaryClientCityState', fieldLabel: 'City, State' },
				{ fieldName: 'customerCount', fieldLabel: 'Customers' },
				{ fieldName: 'salespersonName', fieldLabel: 'Salesperson' },
				{ fieldName: 'alternateSalespersonName', fieldLabel: 'Alternate' },
				{ fieldName: 'lastContactedDate', fieldLabel: 'Last Contacted' },
				{ fieldName: 'lastContactedLabel', fieldLabel: 'Last Contacted How' },
				{ fieldName: 'createdDate', fieldLabel: 'Added' },
			],
		},
	],
	tableSorts: [
		{
			id: 'contacts-name-asc',
			name: 'Name (A-Z)',
			fields: [{ direction: 'ASC', fieldName: 'name', objectTable: 'contact' }],
		},
		{
			id: 'contacts-last-contacted-desc',
			name: 'Recently Contacted',
			fields: [{ direction: 'DESC', fieldName: 'lastContactedDate', objectTable: 'contact' }],
		},
		{
			id: 'contacts-last-contacted-asc',
			name: 'Longest Since Contact',
			fields: [{ direction: 'ASC', fieldName: 'lastContactedDate', objectTable: 'contact' }],
		},
		{
			id: 'contacts-customer-count-desc',
			name: 'Most Customers',
			fields: [{ direction: 'DESC', fieldName: 'customerCount', objectTable: 'contact' }],
		},
		{
			id: 'contacts-created-desc',
			name: 'Newest',
			fields: [{ direction: 'DESC', fieldName: 'createdDate', objectTable: 'contact' }],
		},
	],
};
